(function () {
  const REDUCED = window.matchMedia("(prefers-reduced-motion: reduce)");

  function restartProgress(bar, durationMs) {
    if (!bar) return;
    bar.style.animation = "none";
    bar.style.transform = "scaleX(0)";
    bar.style.transition = "none";
    void bar.offsetWidth;
    if (!durationMs || REDUCED.matches) {
      bar.style.transform = "scaleX(1)";
      return;
    }
    bar.style.transition = "transform " + durationMs + "ms linear";
    bar.style.transform = "scaleX(1)";
  }

  class PebbleProductSlideshow {
    constructor(root) {
      if (!root || root.pebbleSlideshow) return;
      this.root = root;
      this.slider = root.querySelector("[data-pebble-product-slideshow]");
      this.slides = Array.from(root.querySelectorAll(".pebble-product-slideshow__slide"));
      this.controls = root.querySelector(".pebble-product-slideshow__controls");
      this.items = Array.from(root.querySelectorAll(".pebble-product-slideshow__pagination-item"));
      this.combinedText = root.querySelector(".pebble-product-slideshow__pagination-combined-text");
      this.combinedBar = root.querySelector(
        ".pebble-product-slideshow__pagination-combined .pebble-product-slideshow__progress-bar"
      );
      this.durationMs = parseInt(this.controls?.dataset.autoplayMs || this.slider?.dataset.autoplayMs || "5000", 10);
      if (!Number.isFinite(this.durationMs) || this.durationMs < 1500) this.durationMs = 5000;
      this.index = Math.max(
        0,
        this.slides.findIndex((slide) => slide.classList.contains("is-active"))
      );
      this.timer = 0;
      this.touch = null;
      this.visible = true;
      this.bound = false;

      if (this.slides.length === 0) return;
      if (this.index < 0) this.index = 0;

      root.pebbleSlideshow = this;
      if (REDUCED.matches) root.classList.add("is-reduced");

      this.goTo(this.index, true);
      this.bind();
      this.watchVisibility();
      this.play();
    }

    bind() {
      if (this.bound) return;
      this.bound = true;

      this.root.addEventListener("click", (event) => {
        const item = event.target.closest(".pebble-product-slideshow__pagination-item");
        if (item && this.controls?.contains(item)) {
          event.preventDefault();
          const next = parseInt(item.dataset.slideIndex, 10);
          if (!Number.isNaN(next)) this.goTo(next);
          return;
        }
        if (event.target.closest(".pebble-product-slideshow__arrow--prev")) {
          event.preventDefault();
          this.prev();
          return;
        }
        if (event.target.closest(".pebble-product-slideshow__arrow--next")) {
          event.preventDefault();
          this.next();
        }
      });

      const surface = this.slider || this.root;
      surface.addEventListener("pointerdown", (event) => this.onPointerDown(event));
      window.addEventListener("pointerup", (event) => this.onPointerUp(event), { passive: true });
      window.addEventListener("pointercancel", () => {
        this.touch = null;
      });
      surface.addEventListener(
        "touchstart",
        (event) => {
          if (this.touch || event.touches.length !== 1) return;
          const point = event.touches[0];
          this.onPointerDown({
            pointerId: "touch",
            pointerType: "touch",
            button: 0,
            clientX: point.clientX,
            clientY: point.clientY,
            target: event.target,
          });
        },
        { passive: true }
      );
      surface.addEventListener(
        "touchend",
        (event) => {
          if (!this.touch || this.touch.id !== "touch") return;
          const point = event.changedTouches[0];
          this.onPointerUp({
            pointerId: "touch",
            clientX: point.clientX,
            clientY: point.clientY,
          });
        },
        { passive: true }
      );

      document.addEventListener("visibilitychange", () => {
        if (document.hidden) this.pause();
        else if (this.visible) this.play();
      });

      REDUCED.addEventListener?.("change", () => {
        this.root.classList.toggle("is-reduced", REDUCED.matches);
        if (REDUCED.matches) this.pause();
        else if (this.visible) this.play();
      });
    }

    watchVisibility() {
      if (!("IntersectionObserver" in window)) return;
      this.io = new IntersectionObserver(
        (entries) => {
          this.visible = entries.some((entry) => entry.isIntersecting && entry.intersectionRatio > 0.2);
          if (this.visible) this.play();
          else this.pause();
        },
        { threshold: [0, 0.2, 0.5] }
      );
      this.io.observe(this.root);
    }

    onPointerDown(event) {
      if (event.pointerType === "mouse" && event.button !== 0) return;
      if (event.target.closest("button, a")) return;
      this.touch = {
        id: event.pointerId,
        x: event.clientX,
        y: event.clientY,
        at: Date.now(),
      };
      this.pause();
    }

    onPointerUp(event) {
      if (!this.touch || event.pointerId !== this.touch.id) return;
      const dx = event.clientX - this.touch.x;
      const dy = event.clientY - this.touch.y;
      const dt = Date.now() - this.touch.at;
      this.touch = null;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.15 && dt < 800) {
        if (dx < 0) this.next();
        else this.prev();
      }
      if (this.visible) this.play();
    }

    goTo(index, silent) {
      if (!this.slides.length) return;
      const count = this.slides.length;
      this.index = ((index % count) + count) % count;

      this.slides.forEach((slide, i) => {
        slide.classList.toggle("is-active", i === this.index);
        slide.setAttribute("aria-hidden", i === this.index ? "false" : "true");
      });

      this.items.forEach((item, i) => {
        const active = i === this.index;
        item.classList.toggle("is-active", active);
        item.setAttribute("aria-selected", active ? "true" : "false");
        const bar = item.querySelector(".pebble-product-slideshow__progress-bar");
        if (!bar) return;
        if (active && !silent && !REDUCED.matches) restartProgress(bar, this.durationMs);
        else {
          bar.style.transition = "none";
          bar.style.transform = active ? "scaleX(1)" : "scaleX(0)";
        }
      });

      const activeSlide = this.slides[this.index];
      const label =
        activeSlide?.querySelector("[data-pagination-text]")?.textContent?.trim() ||
        this.items[this.index]?.querySelector(".pebble-product-slideshow__pagination-text")?.textContent?.trim() ||
        "";
      if (this.combinedText) this.combinedText.textContent = label;
      if (this.combinedBar && !silent && !REDUCED.matches) restartProgress(this.combinedBar, this.durationMs);

      if (!silent) this.play();
    }

    next() {
      this.goTo(this.index + 1);
    }

    prev() {
      this.goTo(this.index - 1);
    }

    play() {
      this.pause();
      if (this.slides.length < 2 || REDUCED.matches || document.hidden || !this.visible) return;
      const activeItemBar = this.items[this.index]?.querySelector(".pebble-product-slideshow__progress-bar");
      restartProgress(activeItemBar, this.durationMs);
      restartProgress(this.combinedBar, this.durationMs);
      this.timer = window.setTimeout(() => this.next(), this.durationMs);
    }

    pause() {
      if (this.timer) {
        window.clearTimeout(this.timer);
        this.timer = 0;
      }
    }
  }

  function init() {
    document.querySelectorAll(".pebble-product-slideshow").forEach((root) => {
      if (!root.pebbleSlideshow) new PebbleProductSlideshow(root);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  document.addEventListener("shopify:section:load", (event) => {
    const root = event.target.querySelector?.(".pebble-product-slideshow") || event.target;
    if (root?.classList?.contains("pebble-product-slideshow")) new PebbleProductSlideshow(root);
  });
})();
